import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCode, FiSmartphone, FiDatabase, FiTrendingUp } from 'react-icons/fi';

import HeroSection from '../components/HeroSection';
import CourseCard from '../components/CourseCard';
import Button from '../components/Button';
import Testimonials from '../components/Testimonials';
import { SkeletonGrid } from '../components/SkeletonLoader';
import { courseService, partnerService } from '../services/api';
import ScrollAnimation from '../components/ScrollAnimation';

// Page content - Static UI labels and structure
const homePageContent = {
  featuredSection: {
    title: 'Featured',
    titleHighlight: 'Courses',
    subtitle: 'Discover our most popular courses designed to accelerate your tech career'
  },
  whyChooseUs: {
    title: 'Why Choose',
    titleHighlight: 'Service Hub',
    subtitle: 'We provide comprehensive training with real-world experience',
    features: [
      {
        icon: 'FiCode',
        title: 'Industry-Relevant Curriculum',
        description: 'Our courses are designed with input from leading tech companies to ensure you learn the most in-demand skills.'
      },
      {
        icon: 'FiSmartphone',
        title: 'Hands-on Projects',
        description: 'Build real-world projects that showcase your skills and can be added to your professional portfolio.'
      },
      {
        icon: 'FiDatabase',
        title: 'Expert Instructors',
        description: 'Learn from industry professionals with years of experience at top tech companies.'
      },
      {
        icon: 'FiTrendingUp',
        title: 'Career Support',
        description: 'Get personalized career guidance, resume reviews, and job placement assistance.'
      }
    ]
  }
};

const HomeContainer = styled.div`
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
`;

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  
  /* Reduce top padding for first section after hero */
  &:first-of-type {
    padding-top: 3%;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing['3xl']} 0;
    
    &:first-of-type {
      padding-top: 4%;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing['2xl']} 0;
    
    &:first-of-type {
      padding-top: 5%;
    }
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing['3xl']};

  h2 {
    font-size: ${props => props.theme.fontSizes['4xl']};
    color: ${props => props.theme.colors.dark};
    margin-bottom: ${props => props.theme.spacing.lg};

    .highlight {
      color: ${props => props.theme.colors.primary};
    }

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: ${props => props.theme.fontSizes['3xl']};
    }

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes['2xl']};
    }
  }

  p {
    font-size: ${props => props.theme.fontSizes.xl};
    color: ${props => props.theme.colors.gray[600]};
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes.lg};
    }
  }
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: ${props => props.theme.spacing['3xl']};
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 20px;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: block;
    padding: 0;
    overflow: visible;
  }
`;

const MobileCarouselWrapper = styled.div`
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    position: relative;
    overflow: hidden;
    padding: 0 20px 20px 20px;
    margin: 0 -20px;
  }
`;

const MobileCarouselTrack = styled.div`
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: flex;
    gap: 16px;
    padding: 0 20px;
    transition: transform 0.3s ease-out;
    transform: translateX(${props => props.$offset}px);
    
    > * {
      flex: 0 0 calc(100vw - 80px);
      max-width: 350px;
    }
  }
`;

const CarouselDots = styled.div`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 16px;
  }
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.gray[300]};
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
  }
`;

const FeaturesSection = styled(Section)`
  background: ${props => props.theme.colors.gray[100]};
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing['2xl']};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const FeatureCard = styled.div`
  background: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  text-align: center;
  transition: ${props => props.theme.transitions.default};
  position: relative;
  overflow: hidden;
  height: 100%;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.colors.gradient.primary};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows['2xl']};
    border-color: ${props => props.theme.colors.primary}33;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  .icon {
    width: 60px;
    height: 60px;
    background: ${props => props.theme.colors.gradient.primary};
    border-radius: ${props => props.theme.borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.light};
    font-size: ${props => props.theme.fontSizes['2xl']};
    box-shadow: ${props => props.theme.shadows.md};
    transition: ${props => props.theme.transitions.default};
  }

  &:hover .icon {
    transform: scale(1.1) rotate(5deg);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  h3 {
    font-size: ${props => props.theme.fontSizes.xl};
    color: ${props => props.theme.colors.dark};
    margin-bottom: ${props => props.theme.spacing.md};
    flex-shrink: 0;
  }

  p {
    color: ${props => props.theme.colors.gray[600]};
    line-height: 1.6;
    flex-grow: 1;
  }
`;

const PartnersSection = styled(Section)`
  background: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing['4xl']} 0;
  position: relative;
  overflow: hidden;
`;

const MarqueeContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 80px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
    display: ${props => props.$fade ? 'block' : 'none'};
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 1), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, rgba(255, 255, 255, 1), transparent);
  }
`;

const MarqueeTrack = styled.div`
  display: flex;
  align-items: center;
  animation: ${props => props.$animate ? 'scroll 25s linear infinite' : 'none'};
  width: fit-content;
  padding: ${props => props.theme.spacing.xl} 0;

  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }

  &:hover {
    animation-play-state: ${props => props.$animate ? 'paused' : 'running'};
  }
`;

const CompanyLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${props => props.$compact ? '120px' : '140px'};
  width: auto;
  min-width: ${props => props.$compact ? '200px' : '250px'};
  transition: all 0.3s ease;
  position: relative;
  padding: ${props => props.theme.spacing.xl};

  &:hover {
    transform: scale(1.1);
  }

  img.logo {
    height: ${props => props.$compact ? '100px' : '120px'};
    width: auto;
    max-width: ${props => props.$compact ? '220px' : '280px'};
    object-fit: contain;
    transition: all 0.3s ease;
  }

  &:hover img.logo {
    transform: scale(1.05);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-width: 140px;
    height: 80px;
    padding: ${props => props.theme.spacing.md};

    img.logo {
      height: 65px;
      max-width: 150px;
    }
  }
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  align-items: center;
  justify-items: center;
  gap: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.xl} 0;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: ${props => props.theme.spacing.lg};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing.md};
  }
`;

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [partnersList, setPartnersList] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  const { featuredSection, whyChooseUs } = homePageContent;

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle touch events for carousel
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0 && currentSlide < featuredCourses.length - 1) {
        // Swipe left
        setCurrentSlide(currentSlide + 1);
      } else if (distance < 0 && currentSlide > 0) {
        // Swipe right
        setCurrentSlide(currentSlide - 1);
      }
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  const getCarouselOffset = () => {
    const cardWidth = Math.min(window.innerWidth - 80, 350);
    const gap = 16;
    return -(currentSlide * (cardWidth + gap));
  };

  // Icon mapping for features
  const iconMap = {
    FiCode: <FiCode />,
    FiSmartphone: <FiSmartphone />,
    FiDatabase: <FiDatabase />,
    FiTrendingUp: <FiTrendingUp />
  };

  // Map features with icons
  const features = whyChooseUs.features.map(feature => ({
    ...feature,
    icon: iconMap[feature.icon]
  }));

  // Load featured courses from backend API
  useEffect(() => {
    const loadFeaturedCourses = async () => {
      setLoading(true);
      try {
        const courses = await courseService.getFeaturedCourses();
        setFeaturedCourses(courses.slice(0, 3));
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedCourses();
  }, []);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const data = await partnerService.getPartners();
        setPartnersList(Array.isArray(data) ? data : []);
      } catch (e) {
        setPartnersList([]);
      }
    };
    loadPartners();
  }, []);

  return (
    <HomeContainer>
      <HeroSection />
      
      <Section>
        <Container>
          {!loading && (
            <ScrollAnimation animation="fadeInUp">
              <SectionHeader>
                <h2>{featuredSection.title} <span className="highlight">{featuredSection.titleHighlight}</span></h2>
                <p>
                  {featuredSection.subtitle}
                </p>
              </SectionHeader>
            </ScrollAnimation>
          )}
          
          {loading ? (
            <SkeletonGrid type="course" count={3} />
          ) : (
            <>
              {isMobile ? (
                <MobileCarouselWrapper>
                  <MobileCarouselTrack
                    $offset={getCarouselOffset()}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    {featuredCourses.map((course, index) => (
                      <div key={course.id}>
                        <CourseCard
                          course={{
                            ...course,
                            description: course.description,
                            newCourse: course.newCourse,
                            icon: null,
                          }}
                        />
                      </div>
                    ))}
                  </MobileCarouselTrack>
                  <CarouselDots>
                    {featuredCourses.map((_, index) => (
                      <Dot
                        key={index}
                        $active={index === currentSlide}
                        onClick={() => setCurrentSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </CarouselDots>
                </MobileCarouselWrapper>
              ) : (
                <CoursesGrid>
                  {featuredCourses.map((course, index) => (
                    <ScrollAnimation key={course.id} animation="fadeInUp" delay={index * 100}>
                      <CourseCard
                        course={{
                          ...course,
                          description: course.description,
                          newCourse: course.newCourse,
                          icon: null,
                        }}
                      />
                    </ScrollAnimation>
                  ))}
                </CoursesGrid>
              )}
          
              <ScrollAnimation animation="fadeInUp" delay={300}>
                <div style={{ textAlign: 'center' }}>
                  <Button as={Link} to="/courses" variant="primary" size="large">
                    View All Courses
                    <FiArrowRight />
                  </Button>
                </div>
              </ScrollAnimation>
            </>
          )}
        </Container>
      </Section>
      
      {!loading && (
        <FeaturesSection>
          <Container>
            <ScrollAnimation animation="fadeInUp">
              <SectionHeader>
                <h2>{whyChooseUs.title} <span className="highlight">{whyChooseUs.titleHighlight}</span>?</h2>
                <p>
                  {whyChooseUs.subtitle}
                </p>
              </SectionHeader>
            </ScrollAnimation>
            
            <FeaturesGrid>
              {features.map((feature, index) => (
                <ScrollAnimation key={index} animation="fadeInUp" delay={index * 150}>
                  <FeatureCard>
                    <div className="icon">
                      {feature.icon}
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </FeatureCard>
                </ScrollAnimation>
              ))}
            </FeaturesGrid>
          </Container>
        </FeaturesSection>
      )}
      
  {/* Testimonials Section */}
  {!loading && <Testimonials />}
      
      {!loading && (
        <PartnersSection>
          <Container>
            <ScrollAnimation animation="fadeInUp">
              <SectionHeader>
                <h2>Trusted by <span className="highlight">leading companies</span></h2>
                <p>Our students join teams at top organizations around the globe.</p>
              </SectionHeader>
            </ScrollAnimation>
          </Container>
          {(() => {
          const count = partnersList?.length || 0;
          const animate = count > 4; // Marquee for 5+ partners
          if (!count) return null;
          if (animate) {
            return (
              <MarqueeContainer $fade>
                <MarqueeTrack $animate>
                  {[...partnersList, ...partnersList].map((p, idx) => (
                    <CompanyLogo key={idx}>
                      {p.logo && <img className="logo" src={p.logo} alt={p.name} />}
                    </CompanyLogo>
                  ))}
                </MarqueeTrack>
              </MarqueeContainer>
            );
          }
          // Static grid for 1-4 partners
          return (
            <Container>
              <PartnersGrid>
                {partnersList.map((p, idx) => (
                  <CompanyLogo key={idx} $compact>
                    {p.logo && <img className="logo" src={p.logo} alt={p.name} />}
                  </CompanyLogo>
                ))}
              </PartnersGrid>
            </Container>
          );
        })()}
        </PartnersSection>
      )}
    </HomeContainer>
  );
};

export default Home;